import { PackageProviderIcon } from 'components/shared/ppoints/PackageProviderIcon';
import { SettingUrls } from 'constants/SettingsUrls';
import { PackageProviderName } from 'data/ppoints/constants';
import { useFirstSelectedProvider } from 'features/settings/components/ppoints/dashboard/usage-progress-card/hooks/useFirstSelectedProvider';
import useKeepProvidersWithinLimit from 'hooks/plan/useKeepProvidersWithinLimit';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';

interface ProviderLimitExceededModalProps {
    /** Id the opener targets via `commandFor`. */
    id: string;
    /** Carrier limit of the current plan. */
    limit: number;
    planName: string | undefined;
}

/** Radio-style indicator for single-carrier plans; `s-checkbox` covers the multi-carrier case. */
const RadioIndicator: React.FC<{ checked: boolean; disabled: boolean }> = ({ checked, disabled }) => (
    <span
        className={`flex size-4.5 shrink-0 items-center justify-center rounded-full border ${
            checked ? 'border-[6px] border-text' : disabled ? 'border-border-disabled' : 'border-input-border'
        }`}
    />
);

/**
 * Lets the merchant pick which of their active carriers to keep once a plan downgrade left more of them
 * active than the new plan allows. Confirming deactivates the rest and re-activates the app.
 * Opened from `ProviderLimitExceededBanner`'s "Choose carrier" button.
 */
export const ProviderLimitExceededModal: React.FC<ProviderLimitExceededModalProps> = ({ id, limit, planName }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { providers } = useFirstSelectedProvider();
    const { keepProviders, isSaving } = useKeepProvidersWithinLimit();

    const [modal, setModal] = useState<HTMLElementTagNameMap['s-modal'] | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState<PackageProviderName[]>([]);
    const [hasError, setHasError] = useState(false);

    const isSingleChoice = limit === 1;
    const isSelected = (provider: PackageProviderName) => selected.includes(provider);
    const isLimitFilled = selected.length >= limit;

    const toggle = (provider: PackageProviderName) => {
        if (isSingleChoice) {
            setSelected([provider]);
        } else if (isSelected(provider)) {
            setSelected(selected.filter((p) => p !== provider));
        } else if (!isLimitFilled) {
            setSelected([...selected, provider]);
        }
    };

    const handleConfirm = () => {
        setHasError(false);
        keepProviders(selected)
            .then(() => {
                shopify.toast.show(t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.SUCCESS_TOAST'));
                modal?.hideOverlay();
            })
            .catch((error) => {
                console.error('Failed to keep carriers within the plan limit', error);
                setHasError(true);
            });
    };

    const handleUpgrade = () => {
        modal?.hideOverlay();
        navigate(SettingUrls.billing);
    };

    return (
        <s-modal
            ref={setModal}
            id={id}
            size="base"
            heading={t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.TITLE')}
            onShow={() => {
                setIsOpen(true);
                setSelected([]);
                setHasError(false);
            }}
            onHide={() => setIsOpen(false)}
        >
            {isOpen && (
                <div className="flex flex-col gap-4">
                    {hasError && (
                        <s-banner tone="critical" heading={t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.ERROR.TITLE')}>
                            <s-paragraph>{t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.ERROR.DESCRIPTION')}</s-paragraph>
                        </s-banner>
                    )}
                    <div className="flex flex-col gap-1">
                        <s-heading>{t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.HEADING', { count: limit })}</s-heading>
                        <s-paragraph color="subdued">
                            <Trans
                                i18nKey="PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.DESCRIPTION"
                                count={limit}
                                values={{ plan: planName }}
                                components={{ strong: <strong className="text-text" /> }}
                            />
                        </s-paragraph>
                    </div>
                    <div className="overflow-hidden rounded-lg border border-border">
                        <div className="bg-bg-surface-secondary px-3 py-2">
                            <s-text type="strong">
                                {t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.TABLE_HEADING', { count: limit })}
                            </s-text>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {providers.map((provider) => {
                                const checked = isSelected(provider);
                                const disabled = isSaving || (!isSingleChoice && isLimitFilled && !checked);

                                return (
                                    <div
                                        key={provider}
                                        role={isSingleChoice ? 'radio' : 'checkbox'}
                                        aria-checked={checked}
                                        aria-disabled={disabled || undefined}
                                        tabIndex={disabled ? -1 : 0}
                                        onClick={() => !disabled && toggle(provider)}
                                        onKeyDown={(event) => {
                                            if (disabled) return;
                                            if (event.key === ' ' || event.key === 'Enter') {
                                                event.preventDefault();
                                                toggle(provider);
                                            }
                                        }}
                                        className={`flex items-center gap-3 border-t border-border px-3 py-2.5 sm:odd:border-r ${
                                            disabled
                                                ? 'text-text-disabled cursor-not-allowed'
                                                : 'hover-bg cursor-pointer'
                                        }`}
                                    >
                                        {isSingleChoice ? (
                                            <RadioIndicator checked={checked} disabled={disabled} />
                                        ) : (
                                            <s-checkbox checked={checked} disabled={disabled || undefined} />
                                        )}
                                        <PackageProviderIcon provider={provider} size="small" />
                                        <s-text>{PackageProviderService.getDisplayName(provider)}</s-text>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            <s-button
                slot="primary-action"
                variant="primary"
                loading={isSaving || undefined}
                disabled={selected.length === 0 || undefined}
                onClick={handleConfirm}
            >
                {t('PROVIDER_LIMIT_EXCEEDED_BANNER.MODAL.CONFIRM', { count: limit })}
            </s-button>
            <s-button slot="secondary-actions" disabled={isSaving || undefined} onClick={handleUpgrade}>
                {t('PROVIDER_LIMIT_EXCEEDED_BANNER.UPGRADE_PLAN')}
            </s-button>
        </s-modal>
    );
};

export default ProviderLimitExceededModal;
